/* eslint-disable @typescript-eslint/naming-convention */
import { isEmpty, uniq } from "lodash";
import isEqual from "lodash/isEqual";
import memoizeOne from "memoize-one";

import {
  SALE_VALIDATION_ERRORS,
  SaleyardSaleTypes,
  SaleStatus,
  SaleType,
  MarketDesignations,
} from "constants/sale";
import { ROLE_TYPES } from "constants/users";

import { caseInsensitiveCompare } from "./compare";
import { getLivestockSaleId, getSaleyardName } from "./navigation";
import { getAUFormattedDate } from "./timeFormats";

const areEqualSaleIdRoundIdOnly = (cur, next) => {
  const currentMatch = cur[0].match;
  const nextMatch = next[0].match;

  if (currentMatch && nextMatch) {
    return (
      currentMatch.params.saleId === nextMatch.params.saleId &&
      currentMatch.params.roundId === nextMatch.params.roundId
    );
  }
  return false;
};

/**
 * Extract the saleId and roundIDfrom props as a new object. Used to compose reselectors
 * so that unnecessary computation does not occur
 *
 * @param {object} props
 * @returns {object}
 */
export const saleIdRoundIdOnly = memoizeOne(
  props => ({
    saleId: parseInt(props.match.params.saleId, 10),
    roundId: parseInt(props.match.params.roundId, 10),
  }),
  areEqualSaleIdRoundIdOnly,
);

const routeParamsAreEqual = (cur, next) => {
  const currentMatch = cur[0].match;
  const nextMatch = next[0].match;

  return currentMatch && nextMatch
    ? isEqual(currentMatch.params, nextMatch.params)
    : false;
};

export const routeParamsOnly = memoizeOne(
  props => props.match.params,
  routeParamsAreEqual,
);

export const dateSort = (n0, n1) => caseInsensitiveCompare(n1.date, n0.date);
/**
 * Performs validation on a sale prior to being submitted to the api. Returns an array
 * containing all of the appropriate business rules violated, described by SALE_VALIDATION_ERRORS.
 * @param {Sale} updatedSale the values of the sale to either be created or updated
 * @param {Sale[]} allSales the sale values from the state
 * @param {boolean} isSaleyardAdmin is the user a saleyard admin
 * @param {boolean} isLivestockAgent is the user a livestock agent
 * @returns {String[]} {@link SALE_VALIDATION_ERRORS}
 */
export const validateSale = (
  updatedSale,
  allSales,
  isSaleyardAdmin,
  isLivestockAgent,
  isEdit,
) => {
  const {
    livestocksale_id,
    sale_type,
    saleyard_id,
    date,
    species_id,
    pricing_type_id,
  } = updatedSale;
  const validationErrors = [];
  // Check if there is an existing Bobbycalf or saleyard sale with the
  // same details.
  const disAllowDuplicateSaleTypes: SaleType[] = [SaleType.BOBBYCALF];
  // Saleyard admins can (kind of) create multiple saleyard sales.
  // If they already exist, it will create the companion deployment sales.
  if (!isSaleyardAdmin && isLivestockAgent && !isEdit) {
    disAllowDuplicateSaleTypes.push(...SaleyardSaleTypes);
  }
  const conflictingSaleExists = allSales.some(
    sale =>
      disAllowDuplicateSaleTypes.indexOf(sale.sale_type) >= 0 &&
      sale.sale_type === sale_type &&
      sale.date === date &&
      sale.saleyard_id === saleyard_id &&
      sale.species_id === species_id &&
      sale.pricing_type_id === pricing_type_id &&
      sale.livestocksale_id !== livestocksale_id,
  );

  if (conflictingSaleExists) {
    validationErrors.push(SALE_VALIDATION_ERRORS.DUPLICATE_SALE);
  }

  return validationErrors;
};

export function getSaleNameForSale(sale) {
  return `${sale.saleyard_name} ${getAUFormattedDate(new Date(sale.date))}`;
}

/**
 * Returns the effective display title for a sale, using the deployment's
 * display name when present, otherwise falling back to the sale_title,
 * or a default derived from saleyard_name and sale_type.
 */
export function getEffectiveSaleTitle(
  sale: SaleBase | null,
  deploymentSale?: DeploymentSale | null,
): string {
  if (deploymentSale?.display_name) {
    return deploymentSale.display_name;
  }
  if (sale?.sale_title) {
    return sale.sale_title;
  }
  if (!sale?.saleyard_name) {
    return "";
  }
  if (sale.sale_type === SaleType.BOBBYCALF) {
    return sale.saleyard_name;
  }
  if (
    sale.sale_type === SaleType.REFERENCE_SALE &&
    sale.saleyard_name.toLowerCase().endsWith(" reference")
  ) {
    return `${sale.saleyard_name} Sale`;
  }
  // Avoid labels like "Paddock Paddock" when the system saleyard name matches the
  // sale-type string (separate from display_name / OTH work; shared title fallback).
  if (sale.saleyard_name.toLowerCase() === sale.sale_type.toLowerCase()) {
    return sale.saleyard_name;
  }
  return `${sale.saleyard_name} ${sale.sale_type}`;
}

/**
 * @typedef {Object} ApiSale
 * @property {number} livestocksale_id
 * @property {string} saleyard_name
 */

/**
 * Get a Sale Object for the purposes of an API request. A Sale object is comprised of the current Saleyard Name and the ID of the current Livestock Sale
 * @returns {ApiSale}
 */
export const getApiSale = () => ({
  saleyard_name: getSaleyardName(),
  livestocksale_id: getLivestockSaleId(),
});

export function isDeploymentSaleBalanced(
  receivedCount,
  areAllConsignmentsBalanced,
  areAllRoundsBalanced,
) {
  return (
    receivedCount > 0 && areAllConsignmentsBalanced && areAllRoundsBalanced
  );
}

export const getSaleExportSites = sale =>
  uniq(
    sale.deployment_sales.reduce((acc, deploymentSale) => {
      deploymentSale.export_sites.map(exportSite => acc.push(exportSite));
      return acc;
    }, []),
  );

export const getDateDrivenSaleStatus = saleDate => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (saleDate > today) {
    return SaleStatus.FUTURE;
  }
  if (saleDate >= yesterday && saleDate <= today) {
    return SaleStatus.IN_PROGRESS;
  }
  return SaleStatus.PAST;
};

// Non-agents get the date-driven archetype.
export const getSaleStatusArchetype = (sale, role, saleStatuses = {}) => {
  if (role?.type === ROLE_TYPES.STOCK_AGENT) {
    // Resolve the assigned status's archetype from the catalogue; Draft when
    // none is set or the viewer can't see it. Reference sales aren't special-cased.
    const saleStatusId = sale.sale_attributes?.saleStatusId;
    const archetype = saleStatusId
      ? saleStatuses[saleStatusId]?.archetype
      : null;
    return archetype || SaleStatus.DRAFT;
  }
  return getDateDrivenSaleStatus(new Date(sale.date));
};

export function isPrimeSaleSubtype(saleSubType: SaleSubType) {
  return (
    !isEmpty(saleSubType) &&
    saleSubType.marketDesignation === MarketDesignations.PRIME
  );
}

export function isStoreSaleSubtype(saleSubType: SaleSubType) {
  return (
    !isEmpty(saleSubType) &&
    saleSubType.marketDesignation === MarketDesignations.STORE
  );
}

export function bestFitForSale<T extends SaleContext>(
  sale: SaleBase,
  objs: T[],
): T | null {
  if (!objs) {
    return null;
  }

  // select the object that best fits the sale context, aligned with backend LivestockSale.select_best_fit
  const {
    sale_sub_type_id: saleSubTypeId,
    species_id: speciesId,
    sale_type: saleType,
  } = sale;

  let objForSpecies = null;
  let objForSaleType = null;
  let objDefault = null;

  for (const obj of Object.values(objs)) {
    // check highest priority - specific to subtype
    if (obj.saleSubTypeId) {
      if (obj.saleSubTypeId === saleSubTypeId) {
        return obj; // exact subtype match — best possible, return early
      }
    }

    // second priority - specific to species [and sale type] (no subtype)
    else if (obj.speciesId) {
      if (obj.speciesId === speciesId && obj.saleType === saleType) {
        objForSpecies = obj;
      }
    }

    // third priority - only specific to sale type (no subtype, no species)
    else if (obj.saleType) {
      if (obj.saleType === saleType) {
        objForSaleType = obj;
      }
    }

    // lowest priority - not specific to any sale (no sale type, no subtype, no species)
    else {
      objDefault = obj;
    }
  }

  return objForSpecies || objForSaleType || objDefault;
}
